import { Link } from '@tanstack/react-router';
import { useAuth } from '@/contexts/auth';

export function HomePage() {
  const { user } = useAuth();

  return (
    <div>
      {/* Hero */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-dream-600/20 via-transparent to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-display font-bold text-white mb-6">
              Explore the Landscape of Your{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-dream-400 to-purple-400">
                Subconscious Mind
              </span>
            </h1>
            <p className="text-xl text-dream-200 mb-8">
              Record your dreams, receive AI-powered interpretations, and journey deeper through
              interactive conversations with the characters and symbols of your dreamscape.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              {user ? (
                <>
                  <Link to="/dreams/new" className="btn-primary text-lg px-8 py-3">
                    Record a Dream
                  </Link>
                  <Link to="/subconscious" className="btn-secondary text-lg px-8 py-3">
                    Enter the Subconscious
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/signup" className="btn-primary text-lg px-8 py-3">
                    Start Your Journey
                  </Link>
                  <Link to="/symbols" className="btn-secondary text-lg px-8 py-3">
                    Explore Symbols
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <FeatureCard
              title="Dream Journal"
              description="Record your dreams with rich details - emotions, people, places, and objects. Build a personal archive of your nightly adventures."
              icon="journal"
            />
            <FeatureCard
              title="AI Interpretation"
              description="Receive instant analysis powered by Jungian psychology. Discover symbols, archetypes, and hidden meanings in your dreams."
              icon="brain"
            />
            <FeatureCard
              title="Subconscious Chat"
              description="Engage in conversations with your dream characters throughout the day. Watch your dream unfold and evolve in real-time."
              icon="chat"
            />
          </div>
        </div>
      </section>

      {/* Subconscious Preview */}
      <section className="py-20 bg-dream-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-display font-bold text-white mb-4">
                Gamify Your Dreams Throughout the Day
              </h2>
              <p className="text-dream-200 mb-6">
                Your subconscious doesn't stop when you wake up. Our AI-powered orchestration
                engine continues to evolve your dream narrative, sending you notifications at
                key milestones.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-dream-600/30 flex items-center justify-center text-dream-400 text-sm mt-0.5">
                    1
                  </span>
                  <span className="text-dream-100">
                    <strong>Record your dream</strong> - capture the raw experience
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-dream-600/30 flex items-center justify-center text-dream-400 text-sm mt-0.5">
                    2
                  </span>
                  <span className="text-dream-100">
                    <strong>Start a session</strong> - your AI twin and dream characters begin conversing
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-dream-600/30 flex items-center justify-center text-dream-400 text-sm mt-0.5">
                    3
                  </span>
                  <span className="text-dream-100">
                    <strong>Get notified</strong> - receive updates when something interesting happens
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-dream-600/30 flex items-center justify-center text-dream-400 text-sm mt-0.5">
                    4
                  </span>
                  <span className="text-dream-100">
                    <strong>Unlock deeper access</strong> - meditation sessions grant write permissions
                  </span>
                </li>
              </ul>
              <Link to="/signup" className="btn-primary">
                Try the Subconscious
              </Link>
            </div>
            <div className="card bg-dream-900/50 p-0 overflow-hidden">
              <div className="bg-dream-800/50 px-4 py-3 border-b border-dream-700">
                <span className="text-dream-300 text-sm">Subconscious Chat</span>
              </div>
              <div className="p-4 space-y-4">
                <div className="chat-message-narrator">
                  The mist parts to reveal a familiar face...
                </div>
                <div className="chat-message-character">
                  <div className="text-xs text-purple-400 mb-1">The Guide</div>
                  You've been searching for something. I've watched you wander these halls before.
                </div>
                <div className="chat-message-twin">
                  <div className="text-xs text-dream-400 mb-1">Your Twin</div>
                  The paintings on the wall... they keep changing. What are you trying to show me?
                </div>
                <div className="chat-message-narrator">
                  A notification will be sent when a breakthrough occurs...
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function FeatureCard({
  title,
  description,
  icon,
}: {
  title: string;
  description: string;
  icon: 'journal' | 'brain' | 'chat';
}) {
  const icons = {
    journal: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
    ),
    brain: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
    chat: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
    ),
  };

  return (
    <div className="card hover:border-dream-600 transition-colors group">
      <div className="w-14 h-14 rounded-xl bg-dream-600/20 flex items-center justify-center text-dream-400 mb-4 group-hover:bg-dream-600/30 transition-colors">
        {icons[icon]}
      </div>
      <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
      <p className="text-dream-300">{description}</p>
    </div>
  );
}
